// Single source of truth for the password rules used everywhere a password is
// set: the emailed reset/activation link (PasswordResetPage) and the "Change
// Password" modal in Settings. Keep the rules here — never inline them in a
// screen — so the two flows can't drift apart.

export const PASSWORD_MIN_LENGTH = 8;

export const PASSWORD_RULES = [
  {
    key: 'minLength',
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
    en: `At least ${PASSWORD_MIN_LENGTH} characters`,
    ar: `${PASSWORD_MIN_LENGTH} أحرف على الأقل`,
  },
  {
    key: 'hasUpper',
    test: (value) => /[A-Z]/.test(value),
    en: 'One uppercase letter',
    ar: 'حرف إنجليزي كبير واحد على الأقل',
  },
  {
    key: 'hasLower',
    test: (value) => /[a-z]/.test(value),
    en: 'One lowercase letter',
    ar: 'حرف إنجليزي صغير واحد على الأقل',
  },
  {
    key: 'hasNumber',
    test: (value) => /\d/.test(value),
    en: 'One number',
    ar: 'رقم واحد على الأقل',
  },
  {
    key: 'hasSpecial',
    test: (value) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
    en: 'One special character',
    ar: 'رمز خاص واحد على الأقل',
    // Kept out of the label so it can be rendered dir="ltr" and not get
    // reordered when the sentence around it is Arabic.
    hint: '(!@#$%^&*)',
  },
];

/** { minLength: true, hasUpper: false, ... } — one flag per rule. */
export const getPasswordValidations = (password) => {
  const value = String(password ?? '');
  return PASSWORD_RULES.reduce((acc, rule) => {
    acc[rule.key] = rule.test(value);
    return acc;
  }, {});
};

export const isPasswordValid = (password) => {
  const value = String(password ?? '');
  return PASSWORD_RULES.every((rule) => rule.test(value));
};

export const getPasswordRuleLabel = (rule, language) =>
  (language === 'ar' ? rule.ar : rule.en);

/** How many rules the password currently satisfies (0..PASSWORD_RULES.length). */
export const getPasswordScore = (password) =>
  Object.values(getPasswordValidations(password)).filter(Boolean).length;
